import type { Except } from "type-fest";
import { z } from "zod";
import type {
    _OrderEventBaseFactoryParams} from "./_order-base.event";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "./_order-base.event";

import { signatureFactory } from "../signature-factory";

const orderImportedEventZodSchema = _orderEventBaseZodObject.merge(
    z.object({
        type: z.literal("ORDER_IMPORTED"),
        system: z.literal("CHANNEX"),
        // Unique Booking Revision identification record at Channex internal system
        channexId: z.string(),
        channel: z.object({
            //ota_name
            name: z.string(),
            //booking_id
            bookingId: z.string(),
            //unique_id Unique Booking identification record combined from OTA Code and OTA Reservation Code. Usually this value is same for all booking revisions.
            uniqueId: z.string(),
            //system_id Unique message identification record at Booking Source platform, unique per revision. Used to detect have we that message or not.
            systemId: z.string().optional(),
            //ota_reservation_code
            otaReservationCode: z.string(),
        }),
    }),
);

type OrderImportedEvent = z.infer<typeof orderImportedEventZodSchema>;

type FactoryParams = _OrderEventBaseFactoryParams &
    Except<
        OrderImportedEvent,
        "type" | "_id" | "_type" | "_createdAt" | "signature"
    >;

type Params = Except<FactoryParams, "signature">;
const orderImportedEventFactory = (params: Params): OrderImportedEvent => ({
    ..._orderEventBaseFactory({
        orderId: params.orderId,
        orderGroupId: params.orderGroupId,
        _version: params._version,
        providerId: params.providerId,
        providerSlug: params.providerSlug,
        signature: signatureFactory(params.orderId, "ORDER_IMPORTED"),
    }),

    type: "ORDER_IMPORTED",
    system: "CHANNEX",
    channexId: params.channexId,
    channel: {
        name: params.channel.name,
        bookingId: params.channel.bookingId,
        uniqueId: params.channel.uniqueId,
        systemId: params.channel.systemId,
        otaReservationCode: params.channel.otaReservationCode,
    },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isOrderImportedEvent = (event: any): event is OrderImportedEvent => {
    return (
        !!event &&
        event._type === "ORDER_EVENT" &&
        event?.type === "ORDER_IMPORTED"
    );
};

export {
    isOrderImportedEvent,
    orderImportedEventFactory,
    orderImportedEventZodSchema,
};
export type { OrderImportedEvent };
